import { useState } from 'react';
import { X } from 'lucide-react';
import { AsyncUserSelect, Avatar, IconButton, Input } from '@/shared/ui';
import {
  MAX_PERSONNEL_NUMBER,
  userFieldValues,
  type PersonnelNumberMode,
  type UserFieldValue,
} from '@/shared/api/forms';
import styles from './UserField.module.css';

export const UserField: React.FC<{
  id?: string;
  value: unknown;
  multiple?: boolean;
  allowOther?: boolean;
  personnelNumber?: PersonnelNumberMode;
  disabled?: boolean;
  placeholder?: string;
  onChange: (value: UserFieldValue | UserFieldValue[] | undefined) => void;
}> = ({ id, value, multiple = false, allowOther = false, personnelNumber, disabled, placeholder, onChange }) => {
  const picked = userFieldValues(value);
  const [justAdded, setJustAdded] = useState<string | null>(null);

  const keyOf = (v: UserFieldValue) => (v.id ? `id:${v.id}` : `ext:${v.label.toLowerCase()}`);
  const commit = (next: UserFieldValue[]) =>
    onChange(next.length === 0 ? undefined : multiple ? next : next[0]);

  const add = (person: UserFieldValue) => {
    if (picked.some((v) => keyOf(v) === keyOf(person))) return;
    setJustAdded(keyOf(person));
    commit(multiple ? [...picked, person] : [person]);
  };

  const patch = (person: UserFieldValue, change: Partial<UserFieldValue>) =>
    commit(picked.map((v) => (keyOf(v) === keyOf(person) ? { ...v, ...change } : v)));

  const canAdd = multiple || picked.length === 0;

  return (
    <div id={id} className={styles.root}>
      {picked.length > 0 && (
        <ul className={styles.people}>
          {picked.map((person) => (
            <li key={keyOf(person)} className={styles.person}>
              <Avatar name={person.label} seed={person.id ?? person.label} className={styles.avatar} />
              <span className={styles.name}>
                <span className={styles.label} title={person.label}>
                  {person.label}
                </span>
                {person.external && (
                  <span className={styles.tag} title="Нет учётной записи — записан строкой">
                    нет в системе
                  </span>
                )}
              </span>
              {personnelNumber && (
                <Input
                  className={styles.number}
                  value={person.personnelNumber ?? ''}
                  maxLength={MAX_PERSONNEL_NUMBER}
                  placeholder="Табельный №"
                  autoComplete="off"
                  autoFocus={keyOf(person) === justAdded}
                  disabled={disabled}
                  aria-label={`Табельный номер — ${person.label}`}
                  aria-required={personnelNumber === 'required' || undefined}
                  onChange={(e) => patch(person, { personnelNumber: e.target.value || undefined })}
                />
              )}
              <IconButton
                className={styles.remove}
                size="sm"
                label={`Убрать ${person.label}`}
                onClick={() => commit(picked.filter((v) => keyOf(v) !== keyOf(person)))}
                disabled={disabled}
              >
                <X size={14} aria-hidden />
              </IconButton>
            </li>
          ))}
        </ul>
      )}

      {canAdd && (
        <AsyncUserSelect
          placeholder={placeholder || 'ФИО или почта сотрудника'}
          disabled={disabled}
          excludeUserIds={picked.map((v) => v.id).filter((v): v is string => Boolean(v))}
          excludeReason="уже выбран"
          onSelect={(user) => add({ id: user.id, label: user.displayName || user.email || user.id })}
          onCreate={allowOther ? (label) => add({ label, external: true }) : undefined}
          createHint="нет в системе"
        />
      )}
    </div>
  );
};
